/**
 * B-T1 (constitution closure 2026-10-19) — the server's flags on an OPEN unattended operation.
 *
 * Written only by `flag_open_unattended_operations()` (pg_cron, migration 20270206221000): PAST_EXPECTED_END
 * (stated expected end + 30 min grace) and OPEN_AT_CLOSE (still running at the first shop close after it started).
 * Detect and warn only — the operation is never ended for anyone (CORE-AJ :76, CORE-BE :47); the board and the
 * workspace show the flag so the problem is visible before someone reports it (CORE-AD :45, ledger D-H1).
 */
export type UnattendedFlagKind = 'PAST_EXPECTED_END' | 'OPEN_AT_CLOSE';

export interface UnattendedFlagRow {
  operation_id: string;
  flag: string;
  threshold_at?: string | null;
}

export const UNATTENDED_FLAG_LABEL: Record<UnattendedFlagKind, string> = {
  PAST_EXPECTED_END: 'Past expected end — starter notified',
  OPEN_AT_CLOSE: 'Still running at shop close — notified',
};

const ORDER: UnattendedFlagKind[] = ['PAST_EXPECTED_END', 'OPEN_AT_CLOSE'];

export function isUnattendedFlagKind(v: unknown): v is UnattendedFlagKind {
  return v === 'PAST_EXPECTED_END' || v === 'OPEN_AT_CLOSE';
}

/** Unresolved flag rows → operation id → its flags (known kinds only, stable order, no duplicates). */
export function groupUnattendedFlags(rows: readonly UnattendedFlagRow[]): Map<string, UnattendedFlagKind[]> {
  const m = new Map<string, UnattendedFlagKind[]>();
  for (const r of rows) {
    if (!isUnattendedFlagKind(r.flag)) continue;
    const list = m.get(r.operation_id) ?? [];
    if (!list.includes(r.flag)) list.push(r.flag);
    m.set(r.operation_id, list);
  }
  for (const list of m.values()) list.sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
  return m;
}

/**
 * The board's open operations with their flags attached (`flags` on each operation). With no flags at all the SAME
 * map is returned, so a shop with nothing flagged renders exactly as before.
 */
export function attachUnattendedFlags<O extends { id: string }>(
  byTicket: ReadonlyMap<string, readonly O[]>,
  flagsByOp: ReadonlyMap<string, readonly UnattendedFlagKind[]>,
): ReadonlyMap<string, readonly (O & { flags?: UnattendedFlagKind[] })[]> {
  if (flagsByOp.size === 0) return byTicket;
  const out = new Map<string, (O & { flags?: UnattendedFlagKind[] })[]>();
  for (const [ticketId, ops] of byTicket) {
    out.set(ticketId, ops.map((op) => {
      const flags = flagsByOp.get(op.id);
      return flags && flags.length > 0 ? { ...op, flags: [...flags] } : op;
    }));
  }
  return out;
}
